import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import { useData } from '../../context/DataContext';
import PeopleIcon from '@mui/icons-material/People';
import hospitalImage from '../../assets/homepageimages/hospital-image.webp';
import PageHero from './PageHero';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const Team = () => {
  const { state } = useData();
  const { pages, teamMembers } = state;
  const teamData = pages.team;

  // Group team members by department
  const groupedTeamMembers = teamMembers ? teamMembers.reduce((acc, member) => {
    const dept = member.department || 'General';
    if (!acc[dept]) {
      acc[dept] = [];
    }
    acc[dept].push(member);
    return acc;
  }, {}) : {};

  return (
    <Box>
      <PageHero
        src={hospitalImage}
        alt="Our Team"
        title={teamData?.title || 'Our Team'}
        subtitle="Meet our dedicated healthcare professionals"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Description */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h5" paragraph sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
            {teamData?.description || 'Meet our dedicated team of healthcare professionals who work tirelessly to provide exceptional care to our patients.'}
          </Typography>
        </Box>

        {/* Team by Departments */}
        {Object.keys(groupedTeamMembers).length > 0 ? (
          Object.entries(groupedTeamMembers).map(([department, members]) => (
            <Box key={department} sx={{ mb: 8 }}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {department}
                </Typography>
                <Divider sx={{ width: 100, mx: 'auto', mb: 3 }} />
              </Box>
              
              <Grid container spacing={4}>
                {members.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Card sx={{ 
                      height: '100%', 
                      p: 3,
                      textAlign: 'center',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6
                      }
                    }}>
                      <CardContent>
                        <Avatar
                          src={member.image}
                          alt={member.name}
                          sx={{ 
                            width: 140, 
                            height: 140, 
                            mx: 'auto', 
                            mb: 3,
                            boxShadow: 3
                          }}
                        />
                        <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {member.name}
                        </Typography>
                        <Chip 
                          label={member.position} 
                          color="primary" 
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ lineHeight: 1.6 }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(member.bio) }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No team members available at the moment.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Our team information will be updated soon.
            </Typography>
          </Box>
        )}

        {/* Team Values */}
        <Paper sx={{ p: 6, mt: 8, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
          <Typography variant="h4" component="h2" gutterBottom textAlign="center" sx={{ mb: 6, fontWeight: 'bold' }}>
            What Makes Our Team Special
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Expertise
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Highly qualified professionals with years of experience in their respective fields
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Compassion
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Caring professionals who treat every patient with dignity and respect
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Collaboration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Team-based approach to healthcare ensuring comprehensive patient care
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Join Our Team */}
        <Box sx={{ mt: 8, p: 6, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Join Our Team
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 600, mx: 'auto' }}>
            We're always looking for talented healthcare professionals who share our passion for 
            excellence and patient care. If you're interested in joining our team, please visit 
            our careers page to view current opportunities.
          </Typography>
          <Chip 
            label="View Career Opportunities" 
            color="primary" 
            size="large"
            sx={{ fontSize: '1.1rem', py: 1 }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Team; 